<template>
  <v-row no-gutters class="fill-height">
    <v-col class="px-6 pt-2 pb-12 fill-height">
      <h1 class="display-1">
        {{ $t("devices") }}
        <span>
          {{ devicesLength }} {{ $t("total") }}
          <v-btn
          icon
          small
          color="textColor"
          class="refresh"
          @click="refreshData()"
          >
          <v-icon small>mdi-autorenew</v-icon>
        </v-btn>  
        </span>
      </h1>
      <h2 class="subtitle-2">{{ $t("deviceSubtitle") }}</h2>
      <DevicesFilter />
      <div class="pt-5 pb-5 d-flex align-end justify-space-between">
        <v-col class="pb-0" cols="5">
          <v-text-field v-model="model.search" append-icon="mdi-magnify" :label="searchLabel" single-line clearable
            hide-details />
        </v-col>
        <div class="pb-0" cols="4">
          <v-btn         
            color="primary"
            class="#e4e4e9--text" 
            @click="addDevice" rounded>
            {{ $t("devicesAdd") }}
          </v-btn>
        </div>
      </div>
      <v-sheet>
        <v-data-table :headers="tableheaders" :items="devices" :search="model.search" v-model="model.selected"
          fixed-header max-height="450px" item-key="tagId" class="installation-table max-height elevation-1"
          @click:row="selectDevice" :loading="isLoadingTable" :loading-text="$t('loadingData')">
          <template v-slot:[`item.type`]="{ item }">
            <v-icon v-if="item.type">
              {{getIconFromDeviceType(item.type)}}
            </v-icon>
          </template>
          <template v-slot:[`item.address`]="{ item }">
              {{ `${item.address}, ${item.area}` }}
          </template>
          <template v-slot:[`item.onlineState`]="{ item }">
              <v-tooltip top color="gray">
              <template v-slot:activator="{ on, attrs }">
                <v-icon 
                  v-if="item.onlineState" 
                  v-bind="attrs"
                  v-on="on"
                  color="success">
                  mdi-wifi
                </v-icon>
                <v-icon 
                  v-else 
                  v-bind="attrs"
                  v-on="on"
                  color="error">
                  mdi-wifi-alert
                </v-icon>
              </template>
              {{ $t("from") + ' ' + formatDate(item?.last_update) }} 
            </v-tooltip>
          </template>
          <template v-slot:[`item.device_name`]="{ item }">
            <div v-if="item.device_name?.length > 0">
              {{ item.device_name }}
            </div>
            <span v-else>
              -
            </span>
          </template>
          <template v-slot:[`item.notes`]="{ item }">
            <div v-if="item.notes">
              {{ item.notes }}
            </div>
            <span v-else color="black">
              -
            </span>
          </template>
          <template v-slot:[`item.fault`]="{ item }">
            <v-icon v-if="item.fault === 1" color="warning">
              mdi-alert-outline
            </v-icon>
            <v-icon v-else-if="item.fault === 0" color="success">
              mdi-heart
            </v-icon>
            <span v-else color="black">
              -
            </span>
          </template>
          <template v-slot:[`item.last_update`]="{ item }">
              {{ formatDate(item?.last_update) }}
          </template>
          <template v-slot:[`item.client_name`]="{item}">
            <v-tooltip top :disabled="!item.client_is_archived">
              <template v-slot:activator="{ on }">
                <div v-on="on" v-if="item.client_is_archived">
                  {{ item.client_name }} <v-icon color="#ff9e39" > mdi-alert-circle </v-icon>
                </div>
                <div v-on="on" v-else-if="!item.client_is_archived">
                  {{ item.client_name }}
                </div>
              </template>
              <span>{{ $t("client_has_been_deleted") }}</span>
            </v-tooltip>
          </template>
          <template v-slot:no-data>
            {{ $t("devicesEmpty") }}
          </template>
          <!-- does not use deviceInfo, but it needs to be attached to a value in devices, loggedIn is already used -->
          <template v-slot:[`item.id`]="{ item }">
            <Dropdown>
              <DropdownItem title="view" @clicked="selectDevice(item)" />
              <DropdownItem title="edit" @clicked="editDevice(item)" />
              <DropdownItem title="delete" @clicked="deleteDevice(item)" />
            </Dropdown>
          </template>
        </v-data-table>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { commonModule, installationsModule, filterModule, devicesModule } from '@/store/modules/store-accessor'
import Dropdown from '../components/dropdown/Dropdown.vue'
import DropdownItem from '../components/dropdown/DropdownItem.vue'
import DevicesFilter from '@/components/Filters/DevicesFilter.vue'

import { TimeUtil } from '@/helpers/TimeUtil'
import {getIconFromDeviceType} from '@/helpers/data'

@Component({
  components: { Dropdown, DropdownItem, DevicesFilter }
})
export default class Devices extends Vue {
  tableheaders: any [] = [
    { text: this.$t("type"), align: 'left', sortable: true, class: 'row-style', value: 'type' },
    { text: this.$t("serial_number"), align: 'left', class: 'row-style', sortable: true, value: 'serial_no' },
    { text: this.$t("device_id"), align: 'left', class: 'row-style', sortable: true, value: 'device_id' },
    { text: this.$t("device_friendly_name"), align: 'left', class: 'row-style', sortable: true, value: 'device_name' },
    { text: this.$t("fullname"), sortable: true, class: 'row-style', value: 'client_name' },
    { text: this.$t("installation"), sortable: true, class: 'row-style', value: 'address' },
    { text: this.$t("notes"), sortable: true, class: 'row-style', value: 'notes' },
    { text: this.$t("hvac_brand"), sortable: true, class: 'row-style', value: 'brand_model' },
    { text: this.$t("online"), sortable: true, class: 'row-style', value: 'onlineState' },
    { text: this.$t("device_health"), sortable: true, class: 'row-style', value: 'fault' },
    // { text: this.$t("last_update"), sortable: true, class: 'row-style', value: 'last_update' },
    { text: '', align: 'right', sortable: false, class: 'row-style', value: 'id', width: '10px'  } 
  ]

  model: any = {
    search: '',
    selected: [] as string[]
  }

  formatDate (date: Date): string {
    return TimeUtil.formatDateTime(date)
  }

  getIconFromDeviceType(deviceType: string) {
    return getIconFromDeviceType(deviceType);
  }

  get devicesLength() {
    return this.devices.length;
  }

  get searchLabel() {
    return this.$t("devicesSearch")
  }

  get devices(){
    return devicesModule.devices
  }

  get currInstallation(){
    return installationsModule.currentInstallation
  }

  get isLoadingTable() {
    return devicesModule.isLoadingTable;
  }

  async refreshData() {
    devicesModule.getDeviceCount();
    devicesModule.getDevices(devicesModule.filters);
    filterModule.getDevicesFilters();
  }

  async mounted () {
    /*
     TODO: Handle installations properly when UX is clear.
     */
    devicesModule.getDeviceCount();
    devicesModule.getDevices(devicesModule.filters);
    filterModule.getDevicesFilters();
    commonModule.getClientsCombo();
    commonModule.getPaymentTypeCombo();
    commonModule.initSnackbar({});
  }

  async destroyed () {
    devicesModule.clearDevices();
  }

  async selectDevice (device: any) {
    devicesModule.setIsLoadingTable(true);

    await devicesModule.getDeviceInfo(device.device_id);
    await installationsModule.getInstallationInfo(device.installation_id);
    devicesModule.setIsLoadingTable(false);
    commonModule.showSideview({ name: 'devices-sideview' , payload: { installationId: device.installation_id, deviceId: device.device_id, tab: 0 } })
  }

  async addDevice (){
    await installationsModule.getInstallationNextTagId();
    commonModule.showModal({ name: 'add-device-modal' })
  }

  deleteDevice(device: any) {
    commonModule.showModal({ name: 'delete-device-modal', payload: { device: device}})
  }

  async editDevice (device: any) {
    await devicesModule.getDeviceInfo(device.device_id);
    await installationsModule.getInstallationInfo(device.installation_id);
    commonModule.showModal({ name: 'edit-device-modal', payload: { view: "installations"} })
  }
}
</script>

<style scoped lang="scss">

.installation-table:not(thead) {
  cursor: pointer;
  min-width: 600px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}


.max-height {
  max-height: 65vh;
  overflow: auto;
}

.placeholder {
  background-color: grey
}

h1 {
  margin-top: 40px;
  font-size: 35px;
  font-weight: 500;
  line-height: 65px;
}

span {
  margin-left: 10px;
  font-size: 18px;
  font-weight: 400;
  color: $pale-purple;
}
</style>
